import React from "react";

import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import { FormSpy } from "react-final-form";
import { debounce } from "debounce";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import { Button } from "@material-ui/core";

import Page from "components/Layout/Page";
import LoadingButton from "components/LoadingButton";
import FormControl, {
  DropdownFieldControl,
  TextFieldControl,
} from "components/FormControl";
import SubmitButtonControl, {
  CancelButtonControl,
} from "components/FormControl/SubmitButtonControl";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import SimpleField from "components/SimpleField";
import LayoutContainer from "components/Layout/Container";
import Header from "components/Layout/Header";
import PublishButton from "components/PublishButton";
import { SNACKBAR_TYPES } from "components/Snackbar";
import { lengthValidator } from "utils/validators";
import ROUTES from "navigation/routes";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import {
  BackButtonWrapper,
  Container,
  ControlsContainer,
  ControlContainer,
  Column,
  GroupRow,
  Row,
} from "./styles";
import ActiveProgramStore from "stores/ActiveProgramStore";
import {
  ACTIVE_PROGRAM_TYPE,
  ACTIVE_PROGRAM_TYPE_TEXT,
} from "constants/models";
import { getActiveProgramTypeText } from "../../../../utils/publish";
import WorkoutSequenceStore from "stores/WorkoutSequenceStore";
import { getMessage } from "utils/messages";

type LocationState = {
  editMode: boolean;
  workoutSequenceName?: string;
  workoutSequenceId?: number | null;
  readOnly?: boolean;
};

type Props = RouteComponentProps<{ id: string }, {}, LocationState> &
  SnackbarContextProps & {};

@observer
class ActiveProgramDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;
  @observable closeConfirmationIsOpened: boolean = false;
  @observable updateConfirmationIsOpened: boolean = false;
  @observable isEnableWorkoutSequence: boolean = true;
  @observable workoutSequenceSelected: string | null = "";
  @observable formRef: any = null;
  @observable onAccept: () => void = () => {};

  quillEditorRef = React.createRef();

  @computed get workoutSequenceOptions() {
    return WorkoutSequenceStore.sequencesList.map(item => ({
      value: item.id,
      label: item.title,
    }));
  }

  get locationState() {
    return this.props.location.state;
  }

  get isEditMode() {
    return this.locationState?.editMode;
  }

  get isReadOnlyMode() {
    return this.locationState?.readOnly;
  }

  get isNewItem() {
    const { id } = this.props.match.params;

    return id === undefined;
  }

  get workoutSequenceName(): string {
    return WorkoutSequenceStore.currentSequence?.title ?? "";
  }

  get workoutSequenceId(): number | null {
    return WorkoutSequenceStore.currentSequence?.id ?? null;
  }

  get itemId() {
    const { id } = this.props.match.params;

    const parsedId = Number.parseInt(id);
    return typeof parsedId === "number" ? parsedId : undefined;
  }

  get backClickInfo() {
    return " Back to Active Program";
  }

  get backClickPath() {
    return ROUTES.CONTENT_ACTIVE_PROGRAM;
  }

  @computed get displayValues(): any {
    const formData = ActiveProgramStore.details?.formData;

    return {
      ...formData,
      workoutSequenceName: WorkoutSequenceStore.currentSequence?.title,
    };
  }

  @computed get initialValues() {
    if (this.isNewItem) {
      return {};
    } else {
      return {
        ...ActiveProgramStore.details?.formData,
        workoutSequenceName: WorkoutSequenceStore.currentSequence?.title ?? "",
        workoutSequence: WorkoutSequenceStore.currentSequence?.id,
      };
    }
  }

  async componentDidMount() {
    const { history } = this.props;

    await WorkoutSequenceStore.fetchAllSequences();
    if (this.isNewItem) {
      history.replace({ state: { editMode: true } });
    } else {
      await ActiveProgramStore.fetchDetails(this.itemId);
      if (ActiveProgramStore.details?.workoutSequenceId) {
        await WorkoutSequenceStore.fetchSequenceById(
          ActiveProgramStore.details.workoutSequenceId,
        );
      } else {
        WorkoutSequenceStore.clearCurrentSequence();
      }
      this.isEnableWorkoutSequence =
        ActiveProgramStore?.details?.activeProgramType !== "Common" &&
        ActiveProgramStore?.details?.activeProgramType !== "Vip";
    }
  }

  componentWillUnmount() {
    ActiveProgramStore.resetDetails();
    WorkoutSequenceStore.clearCurrentSequence();
  }

  closeConfirmationPopup = () => {
    this.closeConfirmationIsOpened = false;
  };

  onCloseForm = (onAccept: () => void) => {
    if (this.isPopupDirty) {
      this.closeConfirmationIsOpened = true;
      this.onAccept = onAccept;
    } else {
      onAccept();
    }
  };

  onBackClick = () => {
    const onAccept = () => this.props.history.push(this.backClickPath);

    if (this.isEditMode) {
      this.onCloseForm(onAccept);
    } else {
      onAccept();
    }
  };

  onCancelClick = (resetForm: () => void) => {
    if (this.isNewItem) {
      this.onBackClick();
    } else {
      const onAccept = () => {
        this.exitEditMode();
        this.closeConfirmationPopup();
        this.resetIsPopupDirty();
        resetForm();
      };
      this.onCloseForm(onAccept);
    }
  };

  onEditClick = () => {
    const { history } = this.props;
    const path = generatePath(ROUTES.CONTENT_ACTIVE_PROGRAM_DETAILS, {
      id: this.itemId,
    });
    history.replace({
      pathname: path,
      state: {
        editMode: true,
        workoutSequenceName: this.workoutSequenceName,
        workoutSequenceId: this.workoutSequenceId,
      },
    });
  };

  exitEditMode = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_ACTIVE_PROGRAM_DETAILS, {
      id: this.itemId,
    });
    history.replace({ pathname: path, state: { editMode: false } });
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    this.closeUpdateConfirmation();
    this.submit(values);
  };

  onSubmit = (values: any) => {
    const shouldShowUpdateConfirmation = this.isNewItem;

    shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation()
      : this.submit(values);
  };

  onResponseTypeOption = () => {
    return [
      {
        value: ACTIVE_PROGRAM_TYPE.COMMON,
        label: ACTIVE_PROGRAM_TYPE_TEXT.COMMON,
      },
      {
        value: ACTIVE_PROGRAM_TYPE.VIP,
        label: ACTIVE_PROGRAM_TYPE_TEXT.VIP,
      },
      {
        value: ACTIVE_PROGRAM_TYPE.SPECIAL,
        label: ACTIVE_PROGRAM_TYPE_TEXT.SPECIAL,
      },
      {
        value: ACTIVE_PROGRAM_TYPE.COACH,
        label: ACTIVE_PROGRAM_TYPE_TEXT.COACH,
      },
    ];
  };

  submit = async (values: any) => {
    const { setMessage, history } = this.props;
    const { name, value, id, activeProgramType, workoutSequence } = values;

    const activeProgramEnable =
      activeProgramType === "Common" || activeProgramType === "Vip";
    const data = {
      name: name?.trim(),
      value: value?.trim(),
      activeProgramType: activeProgramType?.trim(),
      ...(workoutSequence &&
        activeProgramEnable && {
          workoutSequenceId: workoutSequence,
        }),
    };
    console.log(data);
    try {
      if (this.isNewItem) {
        const payload = { ...data };
        const id = await ActiveProgramStore.createItem(payload);

        if (id) {
          const path = generatePath(ROUTES.CONTENT_ACTIVE_PROGRAM_DETAILS, {
            id,
          });
          history.push({ pathname: path, state: { editMode: false } });
          await ActiveProgramStore.fetchDetails(id);
          if (ActiveProgramStore.details?.workoutSequenceId) {
            await WorkoutSequenceStore.fetchSequenceById(
              ActiveProgramStore.details.workoutSequenceId,
            );
          } else {
            WorkoutSequenceStore.clearCurrentSequence();
          }
        }
      } else {
        const payload = { ...data, id };
        await ActiveProgramStore.editItem(payload);

        this.exitEditMode();
        await ActiveProgramStore.fetchDetails(id);
        if (ActiveProgramStore.details?.workoutSequenceId) {
          await WorkoutSequenceStore.fetchSequenceById(
            ActiveProgramStore.details.workoutSequenceId,
          );
        } else {
          WorkoutSequenceStore.clearCurrentSequence();
        }
      }
      this.resetIsPopupDirty();
    } catch (error) {
      setMessage(getMessage(error?.message), SNACKBAR_TYPES.ERROR);
    }
  };

  onChangeActiveProgram = (value: string, form: any) => {
    this.isEnableWorkoutSequence = value !== "Common" && value !== "Vip";
  };

  componentDidUpdate(prevProps: Props) {
    const currentValue = this.formRef?.getState()?.values?.workoutSequence;
    if (this.isEnableWorkoutSequence && currentValue !== undefined) {
      this.formRef.change("workoutSequence", undefined);
    }
  }

  render() {
    return (
      <LayoutContainer header={<Header controls={<PublishButton />} />}>
        <Page
          title={"Active Program Detail"}
          topControls={
            <BackButtonWrapper>
              <Button
                color="secondary"
                variant="text"
                startIcon={<KeyboardBackspaceIcon />}
                onClick={this.onBackClick}
              >
                {this.backClickInfo}
              </Button>
            </BackButtonWrapper>
          }
          isLoading={ActiveProgramStore.isLoading}
        >
          {this.closeConfirmationIsOpened && (
            <Popup onClose={this.closeConfirmationPopup} withoutExitButton>
              <ConfirmationDialog
                title="Discard Changes"
                text="Are you sure you would like to discard changes? All information will be lost."
                declineText="Cancel"
                acceptText="Discard"
                onDecline={this.closeConfirmationPopup}
                onAccept={this.onAccept}
              />
            </Popup>
          )}
          <FormControl
            onSubmit={this.onSubmit}
            initialValues={this.initialValues}
            keepDirtyOnReinitialize={this.isNewItem}
          >
            {this.updateConfirmationIsOpened && (
              <FormSpy subscription={{ values: true }}>
                {({ values }: { values: any }) => (
                  <Popup
                    onClose={this.closeUpdateConfirmation}
                    withoutExitButton
                  >
                    <ConfirmationDialog
                      title="Save Changes to Production"
                      text="All changes made will be published to production. Are you sure you want to proceed?"
                      acceptText="Proceed"
                      declineText="Cancel"
                      onAccept={this.confirmUpdate(values)}
                      onDecline={this.closeUpdateConfirmation}
                    />
                  </Popup>
                )}
              </FormSpy>
            )}
            <FormSpy
              subscription={{
                dirty: true,
                dirtyFields: true,
                initialValues: true,
                values: true,
              }}
              onChange={debounce(this.onChangeIsPopupDirty, 500)}
            />
            <FormSpy subscription={{}}>
              {({ form }) => {
                if (!this.formRef) {
                  this.formRef = form;
                }
                return null;
              }}
            </FormSpy>

            <Container>
              <ControlsContainer>
                {this.isEditMode ? (
                  <>
                    <ControlContainer>
                      <CancelButtonControl
                        render={this.renderCancelButton}
                        onClick={this.onCancelClick}
                      />
                    </ControlContainer>
                    <ControlContainer>
                      <SubmitButtonControl render={this.renderButton} />
                    </ControlContainer>
                  </>
                ) : (
                  !this.isReadOnlyMode && (
                    <ControlContainer>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={this.onEditClick}
                        fullWidth
                      >
                        Edit
                      </Button>
                    </ControlContainer>
                  )
                )}
              </ControlsContainer>

              <GroupRow>
                <Column>
                  <Row>
                    <Column>
                      {this.isEditMode ? (
                        <TextFieldControl
                          variant="outlined"
                          margin="none"
                          name="name"
                          label="Name"
                          validate={lengthValidator(100)}
                          fullWidth
                          required
                        />
                      ) : (
                        <SimpleField
                          label="Name"
                          value={this.displayValues?.name}
                        />
                      )}
                    </Column>
                    <Column>
                      {this.isEditMode ? (
                        <TextFieldControl
                          variant="outlined"
                          margin="none"
                          name="value"
                          label="Value"
                          validate={lengthValidator(100)}
                          fullWidth
                          required
                        />
                      ) : (
                        <SimpleField
                          label="Value"
                          value={this.displayValues?.value}
                        />
                      )}
                    </Column>
                  </Row>
                  <Row>
                    <Column>
                      <Column>
                        {this.isEditMode ? (
                          <DropdownFieldControl
                            name="activeProgramType"
                            options={this.onResponseTypeOption()}
                            label="Active Program Type"
                            multi={false}
                            isClearable
                            onChangeCallback={this.onChangeActiveProgram}
                            required={false}
                          />
                        ) : (
                          <SimpleField
                            label="Section"
                            value={getActiveProgramTypeText(
                              this.displayValues?.activeProgramType,
                            )}
                          />
                        )}
                      </Column>
                    </Column>
                    <Column>
                      <Column>
                        {this.isEditMode ? (
                          <DropdownFieldControl
                            key="workoutSequence"
                            name="workoutSequence"
                            label="Workout Sequence"
                            options={this.workoutSequenceOptions}
                            multi={false}
                            isClearable
                            required={false}
                            disabled={this.isEnableWorkoutSequence}
                          />
                        ) : (
                          <SimpleField
                            label="Workout Sequence"
                            value={this.displayValues.workoutSequenceName}
                          />
                        )}
                      </Column>
                    </Column>
                  </Row>
                </Column>
              </GroupRow>
            </Container>
          </FormControl>
        </Page>
      </LayoutContainer>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={ActiveProgramStore.isManaging}
      fullWidth
    >
      {this.isNewItem ? "Add" : "Save"}
    </LoadingButton>
  );

  renderCancelButton = ({ onClick }: { onClick: () => void }) => (
    <Button variant="outlined" color="primary" onClick={onClick} fullWidth>
      Cancel
    </Button>
  );
}

export default withGlobalSnackbar(withRouter(ActiveProgramDetails));
